import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function AdminLogin() {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-[80vh] flex items-center justify-center px-6 py-16"
    >
      <div className="w-full max-w-md">
        <p className="label-eyebrow text-center">Artist Studio</p>
        <h1 className="font-serif text-4xl md:text-5xl font-light text-[#2D2926] mt-4 text-center tracking-tight">
          Sign in
        </h1>
        <p className="text-[#5B5653] text-center mt-4 text-sm">
          Private access — for the artist only.
        </p>

        <form
          data-testid="admin-login-form"
          onSubmit={handleSubmit}
          className="mt-12 space-y-8"
        >
          <label className="block">
            <span className="label-eyebrow block mb-2">Email</span>
            <input
              data-testid="admin-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              className="input-line"
            />
          </label>
          <label className="block">
            <span className="label-eyebrow block mb-2">Password</span>
            <div className="relative">
              <input
                data-testid="admin-password-input"
                type={showPass ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="input-line pr-10"
              />
              <button
                type="button"
                data-testid="toggle-password-visibility"
                onClick={() => setShowPass((v) => !v)}
                aria-label={showPass ? "Hide password" : "Show password"}
                className="absolute right-0 top-1/2 -translate-y-1/2 p-2 text-[#5B5653] hover:text-[#E25C46]"
              >
                {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </label>
          {error && (
            <p data-testid="admin-login-error" className="text-sm text-[#B73225]">
              {error}
            </p>
          )}
          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={loading}
            className="btn-primary w-full"
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}
